import { format } from 'date-fns';
import axios from 'helpers/axios';
import { ValidationPattern } from 'helpers/form';
import { useCompany, useOpenState, useToasts, useUser } from 'hooks';
import _ from 'lodash';
import { defaultJobSettings } from 'providers/company';
import React from 'react';
import { useParams } from 'react-router-dom';
import { UserLayout } from 'ui/components/layouts';
import { Dot, DropDownMenu, DropDownMenuItem, Icon, JobRules } from 'ui/components/shared';
import WeekDays from 'ui/components/shared/WeekDays';
import {
  Button,
  ConfirmationModal,
  Container,
  Form,
  Label,
  MultiTextInput,
  Select,
  Spinner,
  TextInput,
  ToggleSwitch,
} from 'ui/components/styled';
import { EmailsList } from './lists';
import { Avatar, JobTitle, ForceGreenMsg } from './styled';

export const EditJob = () => {
  const [job, setJob] = React.useState();
  const [jobHistory, setJobHistory] = React.useState();
  const [categories, setCategories] = React.useState([]);
  const [preventSubmit, setPreventSubmit] = React.useState(false);
  const [preventSave, setPreventSave] = React.useState(true);
  const [processing, setProcessing] = React.useState(false);
  const { errorToast, successToast, removeAllToasts } = useToasts();
  const [modal, openModal, closeModal] = useOpenState();
  const { fetchCompanyCategories } = useCompany();
  const { redirectToDashboard } = useUser();
  const { id } = useParams();
  const containerRef = React.useRef();
  const emailListRef = React.useRef();
  const [weekDays, setWeekDays] = React.useState([true, true, true, true, true, true, true]);

  const fetchJob = async (id) => {
    try {
      const response = await axios.get(`/jobs/${id}/`);

      if (response.status === 200) {
        const data = {
          ...response.data,
          hoursUntilWarning: response.data.hoursUntilWarning || '',
          emailCapacity: response.data.emailCapacity || '',
          rulesLogic: response.data.rulesLogic || {
            passing: 'and',
            failing: 'and',
            warning: 'and',
          },
        };
        setJob(data);
        setWeekDays(data.weekDays);
        setHistory(data);
        const result = await fetchCompanyCategories(data.customer?.company);
        setCategories(result);
      }
    } catch (err) {
      console.error(err);
      redirectToDashboard();
    }
  };

  const setHistory = (job) => {
    setJobHistory({
      name: job.name,
    });
  };

  React.useEffect(async () => {
    if (!id) return;

    await fetchJob(id);
  }, [id]);

  const handleChange = (e) => {
    const { name, type } = e.target;
    const value = type === 'checkbox' ? e.target.checked : e.target.value;
    _.set(job, name, value);
    setJob({ ...job });
    setPreventSave(false);
  };

  const handleRulesChange = (rules) => {
    setJob((job) => ({ ...job, rules }));
    setPreventSave(false);
  };

  const handleRulesLogicChange = (rulesLogic) => {
    setJob((job) => ({ ...job, rulesLogic }));
    setPreventSave(false);
  };

  const handleSubmit = async (e, matchOrphans = false) => {
    e.preventDefault();
    try {
      setPreventSubmit(true);
      if (matchOrphans) {
        setProcessing(true);
      }
      const url = matchOrphans ? `/jobs/${id}/?$matchOrphans=${matchOrphans}` : `/jobs/${id}`;
      const response = await axios.put(url, {
        name: job.name,
        category: job.category.id,
        sender: job.sender,
        hoursUntilWarning: parseInt(job.hoursUntilWarning) || defaultJobSettings.hoursUntilWarning,
        emailCapacity: parseInt(job.emailCapacity) || defaultJobSettings.emailCapacity,
        notifications: job.notifications,
        rules: job.rules,
        rulesLogic: job.rulesLogic,
        weekDays,
      });
      if (response.status === 200) {
        if (job.status !== response.data.status) {
          setJob((job) => ({ ...job, status: response.data.status }));
        }
        emailListRef.current.refresh();
        setHistory(job);
        removeAllToasts();
        successToast('Monitor updated successfully!');
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Edit Monitor.' });
    }
    if (matchOrphans) {
      setProcessing(false);
    }
    setPreventSubmit(false);
  };

  const deleteJob = async () => {
    try {
      setPreventSubmit(true);
      const response = await axios.delete(`/jobs/${id}`);
      if (response.status === 204) {
        successToast('Monitor deleted!');
        redirectToDashboard();
        return;
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Delete Monitor.' });
    }
    setPreventSubmit(false);
  };

  const handleForceGreen = async (e) => {
    e.preventDefault();
    try {
      setPreventSubmit(true);
      const response = await axios.put(`/jobs/${id}/force-green`);
      if (response.status === 200) {
        if (job.status !== response.data.status) {
          setJob((job) => ({
            ...job,
            status: response.data.status,
            isForceGreen: response.data.isForceGreen,
            forceGreenDate: response.data.forceGreenDate,
            forceGreenUser: response.data.forceGreenUser,
          }));
        }
        setHistory(job);
        removeAllToasts();
        successToast('Force green applied successfully!');
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Force Green.' });
    }
    setPreventSubmit(false);
    closeModal('confirm-force-green');
  };

  const handleCancelForceGreen = async (e) => {
    e.preventDefault();
    try {
      setPreventSubmit(true);
      const response = await axios.put(`/jobs/${id}/cancel-force-green`);
      if (response.status === 200) {
        if (job.status !== response.data.status) {
          setJob((job) => ({
            ...job,
            status: response.data.status,
            isForceGreen: response.data.isForceGreen,
          }));
        }
        setHistory(job);
        removeAllToasts();
        successToast('Force green canceled successfully!');
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Force Green.' });
    }
    setPreventSubmit(false);
    closeModal('confirm-cancel-force-green');
  };

  return (
    <UserLayout ref={containerRef}>
      {job && (
        <Form onSubmit={handleSubmit}>
          <Container
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            height="auto"
            position="sticky"
            top="0"
            left="0"
            bg="#fff"
            gap="15px"
            zIndex={1}
            px="l"
            py="m"
          >
            <Container flexDirection="row" alignItems="center" width="auto" height="auto">
              <JobTitle>{jobHistory?.name}</JobTitle>
              <Dot
                status={job.status}
                isForceGreen={job.isForceGreen}
                title={job.status}
                cursor="initial"
                ml="xs"
                mb={0}
              />

              {job.status === 'success' && job.isForceGreen && (
                <>
                  {job.forceGreenDate && job.forceGreenUser && (
                    <ForceGreenMsg>
                      Forced Green on {format(new Date(job.forceGreenDate), 'MM/dd/yyyy')} by{' '}
                      {job.forceGreenUser}
                    </ForceGreenMsg>
                  )}
                  <Button
                    variant="danger"
                    size="small"
                    width="140px"
                    borderRadius="3px"
                    boxShadow="none"
                    onClick={() => openModal('confirm-cancel-force-green')}
                    disabled={preventSubmit}
                    ml="s"
                  >
                    Cancel Force Green
                  </Button>
                </>
              )}
              {job.status !== 'success' && (
                <Button
                  variant="contrast-success"
                  size="small"
                  width="140px"
                  borderRadius="3px"
                  boxShadow="none"
                  onClick={() => openModal('confirm-force-green')}
                  disabled={preventSubmit}
                  ml="s"
                >
                  Force Green
                </Button>
              )}
            </Container>
            <Container flexDirection="row" width="auto" height="auto">
              <Button
                variant="danger"
                width="100px"
                borderRadius="3px"
                boxShadow="none"
                onClick={() => openModal('confirm-delete-job')}
                disabled={preventSubmit}
                mr="s"
              >
                Delete
              </Button>
              <Button
                type="submit"
                width="100px"
                borderRadius="3px"
                boxShadow="none"
                disabled={preventSubmit || preventSave}
                mr="s"
                hasDropDown
                dropDown={
                  <DropDownMenu icon={() => <Icon icon="caret-down-white" size="9px" />}>
                    <DropDownMenuItem
                      onClick={(e) => handleSubmit(e, true)}
                      disabled={preventSubmit || preventSave}
                    >
                      Save & Re-process
                    </DropDownMenuItem>
                  </DropDownMenu>
                }
              >
                Save
                {processing && <Spinner size="30px" centered />}
              </Button>

              <Button
                variant="contrast-primary"
                width="100px"
                borderRadius="3px"
                boxShadow="none"
                onClick={() => redirectToDashboard()}
                disabled={preventSubmit}
              >
                Close
              </Button>
            </Container>
          </Container>
          <Container height="auto" px="l" pt="xxs" pb="m">
            <Container flexDirection="row" alignItems="center" height="auto" mb="l">
              <Avatar src="/assets/img/icons/user.svg" alt="customer" title="Customer" mr="l">
                {job.customer.name}
              </Avatar>
              <Select
                id="category"
                placeholder="Category"
                caretColor="primary"
                variant="outline"
                minHeight="32px"
                width="auto"
                fitSize
                icon={() => (
                  <Icon src="/assets/img/icons/book.svg" alt="category" size="20px" ml="xs" />
                )}
                descriptive
                options={categories.map((category) => ({
                  label: category.name,
                  value: category.id,
                }))}
                value={job.category.id}
                onChange={(option) =>
                  handleChange({
                    target: {
                      name: 'category',
                      value: {
                        id: option.value,
                        name: option.label,
                      },
                    },
                  })
                }
                mr="l"
                required
              />
              <Container flexDirection="row" alignItems="center" width="auto">
                <Avatar src="/assets/img/icons/notification.svg" alt="notifications" mr="xs">
                  {job.notifications?.enabled ? 'Notifications On' : 'Notifications Off'}
                </Avatar>
                <ToggleSwitch
                  id="notifications"
                  name="notifications.enabled"
                  testId="notifications-toggle"
                  size="small"
                  checked={job.notifications?.enabled}
                  onChange={handleChange}
                />
              </Container>
            </Container>
            <Container flexDirection="row" height="auto" gap="15px">
              <Container height="auto" mb="m">
                <Label htmlFor="name" color="tertiary" mb="xs">
                  Name
                </Label>
                <TextInput
                  id="name"
                  name="name"
                  variant="minimal"
                  placeholder="Monitor name"
                  value={job.name}
                  onChange={handleChange}
                  pattern={ValidationPattern.extendedAlphanumeric}
                  title="Please enter a valid monitor name"
                  required
                />
              </Container>
              <Container height="auto" mb="m">
                <Label htmlFor="email" color="tertiary" mb="xs">
                  Email sender
                </Label>
                <TextInput
                  id="email"
                  name="sender.email"
                  type="emailOrDot"
                  variant="minimal"
                  placeholder="Email sender"
                  value={job.sender?.email}
                  onChange={handleChange}
                  required
                />
              </Container>
              <Container height="auto" mb="m">
                <Label htmlFor="hours-until-warning" color="tertiary" mb="xs">
                  Time until warning (hours)
                </Label>
                <Container flexDirection="row" gap="10px">
                  <TextInput
                    id="hours-until-warning"
                    name="hoursUntilWarning"
                    type="number"
                    min="1"
                    placeholder="Hours until warning (optional)"
                    variant="minimal"
                    value={job.hoursUntilWarning}
                    onChange={handleChange}
                    maxWidth="70px"
                  />
                  <WeekDays
                    data-cy="week-days"
                    value={weekDays}
                    setValue={setWeekDays}
                    onClick={() => setPreventSave(false)}
                  />
                </Container>
              </Container>
              <Container height="auto" mb="m">
                <Label htmlFor="email-capacity" color="tertiary" mb="xs">
                  Email capacity
                </Label>
                <TextInput
                  id="email-capacity"
                  name="emailCapacity"
                  type="number"
                  min="1"
                  placeholder="Email capacity"
                  variant="minimal"
                  value={job.emailCapacity}
                  onChange={handleChange}
                />
              </Container>
            </Container>
            <Container flexDirection="row" height="auto" gap="15px" mb="xs">
              {job.notifications?.enabled && (
                <Container width="50%" height="auto" mb="m">
                  <Label htmlFor="notifications" color="tertiary" mb="xs">
                    Notifications to
                  </Label>
                  <MultiTextInput
                    variant="minimal"
                    placeholder="Email receivers"
                    value={job.notifications?.receivers}
                    onChange={(values) =>
                      handleChange({
                        target: {
                          name: 'notifications.receivers',
                          value: values,
                        },
                      })
                    }
                    validateValues={(value) => {
                      const regex = new RegExp(ValidationPattern.email, 'i');
                      const isValid = regex.test(value);
                      if (!isValid) {
                        errorToast('Please enter a valid email address.');
                      }
                      return isValid;
                    }}
                    disabled={!job.notifications?.enabled}
                    required
                  />

                  <Label variant="legend" fontSize="13px" mt="xs">
                    Ps: please add at least one receiver in the field above then hit enter
                  </Label>
                </Container>
              )}
            </Container>
            <JobRules
              rules={job.rules}
              rulesLogic={job.rulesLogic}
              onRulesChange={handleRulesChange}
              onRulesLogicChange={handleRulesLogicChange}
              labelProps={{ fontWeight: '500' }}
              plusButtonProps={{ mb: 0 }}
            />
          </Container>
          <EmailsList ref={emailListRef} job={job} containerRef={containerRef} />
          <ConfirmationModal
            show={modal.isOpen('confirm-delete-job')}
            onClose={closeModal}
            onConfirm={deleteJob}
          >
            Are you sure you want to remove this monitor ?
          </ConfirmationModal>
          <ConfirmationModal
            show={modal.isOpen('confirm-force-green')}
            onClose={closeModal}
            onConfirm={handleForceGreen}
          >
            Are you sure you want to force this monitor to be green?
          </ConfirmationModal>

          <ConfirmationModal
            show={modal.isOpen('confirm-cancel-force-green')}
            onClose={closeModal}
            onConfirm={handleCancelForceGreen}
          >
            Are you sure you want to cancel force green for this monitor?
          </ConfirmationModal>
        </Form>
      )}
    </UserLayout>
  );
};
